import React from 'react';
import { WyscoutPlayerFixtureFilterOptions } from '@/services/api';
import AdvancedFilterGroup from './AdvancedFilterGroup';
import { WyscoutMatchFilters } from './wyscoutMatchFilters';

interface WyscoutAdvancedFiltersProps {
  values: WyscoutMatchFilters;
  options: WyscoutPlayerFixtureFilterOptions;
  onChange: (patch: Partial<WyscoutMatchFilters>) => void;
}

const inputClass = 'min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const choiceClass = 'flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800';
const pillClass = 'min-h-[44px] rounded-full border px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

const toggleValue = <T extends string | number>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter(current => current !== value) : [...values, value];

const namedOptions = (options: Array<{ id: number; name: string | null }>) => options.flatMap(option => {
  const label = option.name?.trim();
  return label ? [{ value: option.id, label }] : [];
});

const CheckboxList = <T extends string | number>({
  values,
  items,
  onChange,
}: {
  values: T[];
  items: Array<{ value: T; label: string }>;
  onChange: (values: T[]) => void;
}) => (
  <div className="grid gap-1 sm:grid-cols-2">
    {items.map(item => (
      <label key={String(item.value)} className={choiceClass}>
        <input
          type="checkbox"
          className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
          checked={values.includes(item.value)}
          onChange={() => onChange(toggleValue(values, item.value))}
        />
        <span>{item.label}</span>
      </label>
    ))}
  </div>
);

const PillGroup = <T extends string>({
  values,
  items,
  onChange,
}: {
  values: T[];
  items: Array<{ value: T; label: string }>;
  onChange: (values: T[]) => void;
}) => (
  <div className="flex flex-wrap gap-2">
    {items.map(item => {
      const selected = values.includes(item.value);
      return (
        <button
          key={item.value}
          type="button"
          className={`${pillClass} ${selected ? 'border-blue-600 bg-blue-600 text-white dark:border-blue-400 dark:bg-blue-500' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'}`}
          aria-pressed={selected}
          onClick={() => onChange(toggleValue(values, item.value))}
        >
          {item.label}
        </button>
      );
    })}
  </div>
);

const WyscoutAdvancedFilters: React.FC<WyscoutAdvancedFiltersProps> = ({ values, options, onChange }) => {
  const clubs = namedOptions(options.clubs);
  const seasons = namedOptions(options.seasons);
  const competitions = namedOptions(options.competitions);
  const positions = options.positions.filter(position => position.trim().length > 0);

  return <div className="space-y-6">
    {(seasons.length > 0 || clubs.length > 0) && <section aria-label="Season and club" className="space-y-5">
      <h6 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Season and club</h6>
      {seasons.length > 0 && (
        <AdvancedFilterGroup label="Season" active={values.seasonIds.length > 0} onClear={() => onChange({ seasonIds: [] })}>
          <CheckboxList values={values.seasonIds} items={seasons} onChange={seasonIds => onChange({ seasonIds })} />
        </AdvancedFilterGroup>
      )}
      {clubs.length > 0 && (
        <AdvancedFilterGroup label="Club" active={values.clubIds.length > 0} onClear={() => onChange({ clubIds: [] })}>
          <CheckboxList values={values.clubIds} items={clubs} onChange={clubIds => onChange({ clubIds })} />
        </AdvancedFilterGroup>
      )}
    </section>}

    <section aria-label="Match context" className="space-y-5">
      <h6 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Match context</h6>
      {competitions.length > 0 && (
        <AdvancedFilterGroup label="Competition" active={values.competitionIds.length > 0} onClear={() => onChange({ competitionIds: [] })}>
          <CheckboxList values={values.competitionIds} items={competitions} onChange={competitionIds => onChange({ competitionIds })} />
        </AdvancedFilterGroup>
      )}
      <AdvancedFilterGroup label="Opponent" active={values.opponent.trim().length > 0} onClear={() => onChange({ opponent: '' })}>
        <label className="sr-only" htmlFor="wyscout-opponent-filter">Opponent</label>
        <input id="wyscout-opponent-filter" className={inputClass} type="search" placeholder="Search opponent" value={values.opponent} onChange={event => onChange({ opponent: event.target.value })} />
      </AdvancedFilterGroup>
      <AdvancedFilterGroup label="Venue" active={values.venues.length > 0} onClear={() => onChange({ venues: [] })}>
        <PillGroup values={values.venues} items={[{ value: 'home', label: 'Home' }, { value: 'away', label: 'Away' }, { value: 'neutral', label: 'Neutral' }]} onChange={venues => onChange({ venues })} />
      </AdvancedFilterGroup>
      <AdvancedFilterGroup label="Result" active={values.results.length > 0} onClear={() => onChange({ results: [] })}>
        <PillGroup values={values.results} items={[{ value: 'win', label: 'Win' }, { value: 'draw', label: 'Draw' }, { value: 'loss', label: 'Loss' }]} onChange={results => onChange({ results })} />
      </AdvancedFilterGroup>
      <AdvancedFilterGroup label="Date" active={Boolean(values.dateFrom || values.dateTo)} onClear={() => onChange({ dateFrom: null, dateTo: null })}>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm text-gray-700 dark:text-gray-300">From date<input type="date" min={options.earliestDate || undefined} max={values.dateTo || options.latestDate || undefined} className={`${inputClass} mt-1`} value={values.dateFrom || ''} onChange={event => onChange({ dateFrom: event.target.value || null })} /></label>
          <label className="text-sm text-gray-700 dark:text-gray-300">To date<input type="date" min={values.dateFrom || options.earliestDate || undefined} max={options.latestDate || undefined} className={`${inputClass} mt-1`} value={values.dateTo || ''} onChange={event => onChange({ dateTo: event.target.value || null })} /></label>
        </div>
      </AdvancedFilterGroup>
    </section>

    <section aria-label="Player context" className="space-y-5">
      <h6 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Player context</h6>
      {positions.length > 0 && (
        <AdvancedFilterGroup label="Position" active={values.positions.length > 0} onClear={() => onChange({ positions: [] })}>
          <CheckboxList values={values.positions} items={positions.map(position => ({ value: position, label: position.toUpperCase() }))} onChange={positions => onChange({ positions })} />
        </AdvancedFilterGroup>
      )}
      <AdvancedFilterGroup label="Cards" active={values.cards !== 'all'} onClear={() => onChange({ cards: 'all' })}>
        <select className={inputClass} aria-label="Cards" value={values.cards} onChange={event => onChange({ cards: event.target.value as WyscoutMatchFilters['cards'] })}>
          <option value="all">Any discipline</option><option value="any">Any card</option><option value="yellow">Yellow card</option><option value="red">Red card</option>
        </select>
      </AdvancedFilterGroup>
      <AdvancedFilterGroup label="Statistics status" active={values.statsStatuses.length > 0} onClear={() => onChange({ statsStatuses: [] })}>
        <CheckboxList
          values={values.statsStatuses}
          items={[{ value: 'available', label: 'Available' }, { value: 'unavailable', label: 'Unavailable' }, { value: 'failed', label: 'Failed' }, { value: 'ignored', label: 'Ignored' }]}
          onChange={statsStatuses => onChange({ statsStatuses })}
        />
      </AdvancedFilterGroup>
    </section>
  </div>;
};

export default WyscoutAdvancedFilters;
